import { PageCanvas } from "@/components/page-canvas";
import { Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import JobWorkspace from "@/components/job-workspace";
import { Button } from "@/components/ui/button";
import {
  RecordDescription,
  RecordMetadata,
  RecordTitle,
} from "@/components/record-link";
import { TriangleAlertIcon } from "lucide-react";

import { dateTime, money } from "../jobs/types";
import {
  locationJobPath,
  locationJobPaymentPostingsPath,
  newLocationJobPaymentPostingPath,
  newLocationJobAdvanceRequestPath,
} from "@/routes";

type Pagination = { page: number; pages: number };
type Posting = {
  id: string;
  kind: string;
  amount_minor: string;
  method: string;
  reference: string;
  reason: string;
  occurred_at: string;
  recorded_at: string;
  actor: string | null;
  advance_request_id: string | null;
};
type Advance = {
  id: string;
  kind: string;
  requested_minor: string;
  outstanding_minor: string;
  basis_points: number | null;
  due_at: string;
  reason: string;
  actor: string | null;
  recorded_at: string;
  overdue: boolean;
};

export default function Payments({
  job_id,
  currency,
  time_zone,
  summary,
  postings,
  requests,
  pagination,
  requests_pagination,
  requestable,
}: {
  job_id: string;
  currency: string;
  time_zone: string;
  summary: Record<
    "total_minor" | "received_minor" | "outstanding_minor" | "credit_minor",
    string
  >;
  postings: Posting[];
  requests: Advance[];
  pagination: Pagination;
  requests_pagination: Pagination;
  requestable: boolean;
}) {
  const { auth, i18n } = usePage().props;
  const { t } = useTranslation();
  if (!auth.location) return null;
  const location = auth.location.id;
  const ledgerContext = {
    page: pagination.page > 1 ? pagination.page : undefined,
    requests_page:
      requests_pagination.page > 1 ? requests_pagination.page : undefined,
  };
  const amount = (minor: string) => money(minor, currency, i18n.locale);
  const time = (value: string) => dateTime(value, time_zone, i18n.locale);
  const pages = (value: Pagination, advances: boolean) =>
    value.pages > 1 && (
      <nav
        className="flex flex-wrap gap-4"
        aria-label={t(advances ? "payments.requests" : "payments.entries")}
      >
        {[
          value.page > 1 && value.page - 1,
          value.page < value.pages && value.page + 1,
        ].map(
          (page, index) =>
            page && (
              <Link
                key={index}
                className="underline"
                href={locationJobPaymentPostingsPath(location, job_id, {
                  page: advances ? pagination.page : page,
                  requests_page: advances ? page : requests_pagination.page,
                })}
              >
                {t(index === 0 ? "customers.previous" : "customers.next")}
              </Link>
            ),
        )}
      </nav>
    );
  return (
    <JobWorkspace
      primaryAction={
        <Button
          render={
            <Link
              href={newLocationJobPaymentPostingPath(
                location,
                job_id,
                ledgerContext,
              )}
            />
          }
        >
          {t("payments.record")}
        </Button>
      }
      breadcrumbs={[
        {
          title: t("payments.title"),
          href: locationJobPaymentPostingsPath(location, job_id),
        },
      ]}
    >
      <Head title={t("payments.title")} />
      <PageCanvas padded={false}>
        <h2 className="text-page font-display">{t("payments.title")}</h2>
        <p className="text-muted-foreground text-sm">
          {t("payments.external_help")}
        </p>
        <dl className="grid gap-4 sm:grid-cols-2">
          {(["total", "received", "outstanding", "credit"] as const).map(
            (name) => (
              <div key={name}>
                <dt className="text-muted-foreground text-sm">
                  {t(`payments.${name}`)}
                </dt>
                <dd data-money={name}>
                  <RecordTitle className="no-underline">
                    {amount(summary[`${name}_minor`])}
                  </RecordTitle>
                </dd>
              </div>
            ),
          )}
        </dl>
        <div className="flex flex-wrap gap-3">
          {requestable && (
            <Button
              variant="outline"
              render={
                <Link
                  href={newLocationJobAdvanceRequestPath(
                    location,
                    job_id,
                    ledgerContext,
                  )}
                />
              }
            >
              {t("payments.request")}
            </Button>
          )}
        </div>
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{t("payments.requests")}</h2>
          {requests.length === 0 && <p>{t("payments.no_requests")}</p>}
          {requests.map((request) => (
            <article
              key={request.id}
              className="grid gap-4 rounded-lg border p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
            >
              <div className="min-w-0">
                <RecordTitle className="no-underline">
                  {request.reason}
                </RecordTitle>
              </div>
              <div className="space-y-2 text-sm">
                {request.overdue && (
                  <p
                    role="alert"
                    className="flex items-start gap-2 font-semibold text-destructive"
                  >
                    <TriangleAlertIcon
                      aria-hidden="true"
                      className="mt-0.5 size-4 shrink-0"
                    />
                    {t("payments.overdue")}
                  </p>
                )}
                <RecordDescription>
                  {t("payments.requested")}:{" "}
                  <span className="font-semibold">
                    {amount(request.requested_minor)}
                  </span>
                  {request.basis_points !== null
                    ? ` (${request.basis_points / 100}%)`
                    : ""}
                </RecordDescription>
                <RecordDescription>
                  {t("payments.request_remaining")}:{" "}
                  {amount(request.outstanding_minor)}
                </RecordDescription>
                <RecordMetadata>
                  {t("payments.due_at")}: {time(request.due_at)} · {time_zone}
                </RecordMetadata>
                <RecordMetadata>
                  {request.actor} · {t("payments.recorded_at")}:{" "}
                  {time(request.recorded_at)}
                </RecordMetadata>
                {BigInt(request.outstanding_minor) > 0n && (
                  <Button
                    variant="outline"
                    render={
                      <Link
                        href={newLocationJobPaymentPostingPath(
                          location,
                          job_id,
                          { advance_request_id: request.id, ...ledgerContext },
                        )}
                      />
                    }
                  >
                    {t("payments.receive_advance")}
                  </Button>
                )}
              </div>
            </article>
          ))}
          {pages(requests_pagination, true)}
        </section>
        <section className="space-y-4">
          <h2 className="text-lg font-semibold">{t("payments.entries")}</h2>
          {postings.length === 0 && <p>{t("payments.empty")}</p>}
          {postings.map((posting) => (
            <article
              key={posting.id}
              className="grid gap-4 border-b py-4 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
            >
              <div className="min-w-0 space-y-1">
                <RecordTitle className="no-underline">
                  {amount(posting.amount_minor)}
                </RecordTitle>
                <RecordMetadata>
                  {t(`payments.kinds.${posting.kind}`)}
                </RecordMetadata>
              </div>
              <div className="space-y-2 break-words text-sm">
                <RecordDescription>
                  {t(`payments.methods.${posting.method}`)} ·{" "}
                  {posting.reference}
                </RecordDescription>
                <RecordDescription>{posting.reason}</RecordDescription>
                {posting.advance_request_id && (
                  <RecordMetadata>{t("payments.allocated")}</RecordMetadata>
                )}
                <RecordDescription>
                  {t("payments.occurred_at")}: {time(posting.occurred_at)} ·{" "}
                  {time_zone}
                </RecordDescription>
                <RecordMetadata>
                  {t("payments.recorded_at")}: {time(posting.recorded_at)} ·{" "}
                  {posting.actor}
                </RecordMetadata>
              </div>
            </article>
          ))}
          {pages(pagination, false)}
        </section>
        <Link
          className="block underline"
          href={locationJobPath(location, job_id)}
        >
          {t("jobs.confirmed")}
        </Link>
      </PageCanvas>
    </JobWorkspace>
  );
}
